import Alert from "@cloudscape-design/components/alert"
import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import Icon, { type IconProps } from "@cloudscape-design/components/icon"
import Link from "@cloudscape-design/components/link"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import StatusIndicator, {
  type StatusIndicatorProps,
} from "@cloudscape-design/components/status-indicator"
import Steps, { type StepsProps } from "@cloudscape-design/components/steps"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { useMemo, useState } from "react"
import { RunStatus } from "@/generated/sdk"
import Flex from "../../../components/shared/Flex"
import { useQuarantineData } from "../../../hooks/queries/useIngestion"
import { ingestionApiService, QuarantineRecord, IngestionRun } from "../../../services/ingestionApi"

const STEP_ICON: Record<string, { name: IconProps.Name; variant: IconProps.Variant }> = {
  success: { name: "status-positive", variant: "success" },
  error: { name: "status-negative", variant: "error" },
  warning: { name: "status-warning", variant: "warning" },
  "in-progress": { name: "status-in-progress", variant: "link" },
  pending: { name: "status-pending", variant: "subtle" },
}

interface QuarantineRecordsTableProps {
  studyId: string
  runId: string
  /**
   * The run's ingestion spec (domain). Required so the record-history timeline is always scoped to
   * the run's own domain and can never fall back to the cross-domain behavior (#1074).
   */
  specId: string
  onClose: () => void
}

interface RecordHistory {
  run: IngestionRun
  record: QuarantineRecord
  recordCount?: number
}

const QuarantineRecordsTable = ({
  studyId,
  runId,
  specId,
  onClose,
}: QuarantineRecordsTableProps) => {
  const [selectedRecord, setSelectedRecord] = useState<QuarantineRecord | null>(null)
  const [recordHistory, setRecordHistory] = useState<RecordHistory[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const {
    data: quarantineData,
    isLoading: loading,
    error: quarantineError,
  } = useQuarantineData(studyId, runId)
  const records = useMemo(() => quarantineData?.records ?? [], [quarantineData])
  const totalFailed = quarantineData?.totalFailed
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const error = quarantineError
    ? quarantineError instanceof Error
      ? quarantineError.message
      : "Failed to load quarantine data"
    : null

  // Derive display columns and primary-key columns from the record shape.
  const { columns, pkColumns } = useMemo(() => {
    if (records.length === 0) return { columns: [] as string[], pkColumns: [] as string[] }
    const allKeys = new Set<string>()
    records.forEach((record) => Object.keys(record).forEach((key) => allKeys.add(key)))
    const resultKey = "DataQualityEvaluationResult"
    const dataKeys = [...allKeys].filter((k) => k !== resultKey)
    const visibleKeys = dataKeys.filter(
      (k) => !k.startsWith("DataQuality") && !k.startsWith("_ctm"),
    )
    return {
      columns: allKeys.has(resultKey) ? [...visibleKeys, resultKey] : visibleKeys,
      pkColumns: dataKeys.slice(0, 2),
    }
  }, [records])

  const isRecordFailed = (record: QuarantineRecord): boolean => {
    return record.DataQualityEvaluationResult === "Failed"
  }

  const failedRecordsCount = records.filter(isRecordFailed).length

  const fetchRecordHistory = async (record: QuarantineRecord) => {
    setSelectedRecord(record)
    setHistoryLoading(true)
    setRecordHistory([])

    try {
      // Scope to the current run's spec (domain) so the timeline only reflects same-domain runs;
      // without this, runs from other domains that share STUDYID/USUBJID leak in (#1074).
      const { runs } = await ingestionApiService.listRuns(studyId, { specId })
      const allRuns = runs.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )

      const history: RecordHistory[] = []
      for (const run of allRuns) {
        try {
          if (run.status === RunStatus.Failed || run.status === RunStatus.PartiallyCompleted) {
            const { records: qRecords } = await ingestionApiService.getQuarantineData(
              studyId,
              run.runId,
              200,
            )
            const match = qRecords.find((qr) =>
              pkColumns.every((pk) => String(qr[pk]) === String(record[pk])),
            )
            if (match) history.push({ run, record: match, recordCount: qRecords.length })
          } else if (run.status === RunStatus.Completed) {
            history.push({ run, record: {} as QuarantineRecord, recordCount: 0 })
          }
        } catch {
          /* skip */
        }
      }

      setRecordHistory(
        history.sort(
          (a, b) => new Date(b.run.createdAt).getTime() - new Date(a.run.createdAt).getTime(),
        ),
      )
    } catch (err) {
      console.error("Failed to fetch history:", err)
    } finally {
      setHistoryLoading(false)
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    setExportError(null)
    try {
      const { downloadUrl } = await ingestionApiService.exportQuarantineData(studyId, runId)
      // Anchor download rather than navigating the SPA away (the presigned GET is an attachment).
      const a = document.createElement("a")
      a.href = downloadUrl
      a.rel = "noopener"
      a.click()
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Failed to export quarantine data")
    } finally {
      setExporting(false)
    }
  }

  const formatCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return "—"
    if (typeof value === "object") return JSON.stringify(value)
    return String(value as string | number | boolean)
  }

  const getRecordLabel = (record: QuarantineRecord, total: number): string => {
    const isFailed = isRecordFailed(record)
    return isFailed
      ? `Failed record of ${total} quarantined`
      : `Companion record of ${total} quarantined`
  }

  // Group records by primary key values for duplicate detection
  const groupRecordsByPK = (records: QuarantineRecord[]) => {
    const groups = new Map<string, QuarantineRecord[]>()

    records.forEach((record) => {
      const pkKey = pkColumns
        .map((col) => {
          const v = record[col]
          return v == null
            ? ""
            : typeof v === "object"
              ? JSON.stringify(v)
              : String(v as string | number | boolean)
        })
        .join("|")
      if (!groups.has(pkKey)) {
        groups.set(pkKey, [])
      }
      groups.get(pkKey)!.push(record)
    })

    return groups
  }

  const recordGroups = groupRecordsByPK(records)
  const duplicateGroups = Array.from(recordGroups.entries()).filter(
    ([_, group]) => group.length > 1,
  )

  const isDuplicateRecord = (record: QuarantineRecord): boolean => {
    const pkKey = pkColumns
      .map((col) => {
        const v = record[col]
        return v == null
          ? ""
          : typeof v === "object"
            ? JSON.stringify(v)
            : String(v as string | number | boolean)
      })
      .join("|")
    return (recordGroups.get(pkKey)?.length || 0) > 1
  }

  const runStatusIndicator = (status: RunStatus): StatusIndicatorProps.Type =>
    status === RunStatus.Completed ? "success" : status === RunStatus.Failed ? "error" : "warning"

  const columnDefinitions: TableProps.ColumnDefinition<QuarantineRecord>[] = [
    {
      id: "_status",
      header: "Status",
      cell: (record) => (
        <SpaceBetween direction="horizontal" size="xxs">
          <StatusIndicator type={isRecordFailed(record) ? "error" : "stopped"} />
          {isDuplicateRecord(record) && <Badge color="severity-medium">duplicate</Badge>}
        </SpaceBetween>
      ),
    },
    ...columns.map((col) => ({
      id: col,
      header: col,
      cell: (record: QuarantineRecord) => {
        if (col === "DataQualityEvaluationResult") {
          const value = formatCellValue(record[col])
          return (
            <StatusIndicator type={value === "Failed" ? "error" : "success"}>
              {value}
            </StatusIndicator>
          )
        }
        if (col === pkColumns[0]) {
          return (
            <Link onFollow={() => void fetchRecordHistory(record)}>
              {formatCellValue(record[col])}
            </Link>
          )
        }
        return formatCellValue(record[col])
      },
    })),
  ]

  return (
    <Modal
      visible
      onDismiss={onClose}
      size="max"
      header={
        <Header
          counter={
            totalFailed !== undefined
              ? `(${failedRecordsCount} failed of ${totalFailed} quarantined)`
              : undefined
          }
        >
          Quarantined Records
        </Header>
      }
    >
      {error ? (
        <Alert type="error">{error}</Alert>
      ) : (
        <SpaceBetween size="s">
          {exportError && <Alert type="error">{exportError}</Alert>}
          <Box textAlign="right">
            <Button
              onClick={() => void exportCsv()}
              disabled={records.length === 0}
              loading={exporting}
            >
              Export CSV
            </Button>
          </Box>
          {records.length > 0 && (
            <Flex>
              <SpaceBetween direction="horizontal" size="l">
                <StatusIndicator type="error">Failed ({failedRecordsCount})</StatusIndicator>
                <StatusIndicator type="stopped">
                  Companion ({records.length - failedRecordsCount})
                </StatusIndicator>
                {duplicateGroups.length > 0 && (
                  <StatusIndicator type="warning">
                    Duplicate groups ({duplicateGroups.length})
                  </StatusIndicator>
                )}
              </SpaceBetween>
              <Box color="text-body-secondary" fontSize="body-s">
                Click a record to see its history
              </Box>
            </Flex>
          )}
          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <Table
              variant="embedded"
              stickyHeader
              stickyColumns={{ first: 1 }}
              columnDefinitions={columnDefinitions}
              items={records}
              loading={loading}
              loadingText="Loading quarantine data..."
              trackBy={(record) => JSON.stringify(record)}
              empty={
                <Box textAlign="center" color="inherit">
                  No quarantine data available.
                </Box>
              }
            />
          </div>
        </SpaceBetween>
      )}

      {selectedRecord && (
        <Modal
          visible
          onDismiss={() => setSelectedRecord(null)}
          header="Record History"
          size="medium"
        >
          <SpaceBetween size="l">
            <div>
              <Box variant="awsui-key-label">Primary Key</Box>
              <Box>{pkColumns.map((pk) => `${pk}: ${String(selectedRecord[pk])}`).join(", ")}</Box>
              <Box
                margin={{ top: "s" }}
                color={
                  isRecordFailed(selectedRecord) ? "text-status-error" : "text-status-inactive"
                }
                fontSize="body-s"
              >
                {getRecordLabel(selectedRecord, totalFailed ?? records.length)}
              </Box>
            </div>

            {historyLoading ? (
              <Box textAlign="center" color="text-status-inactive" padding="l">
                Loading history...
              </Box>
            ) : recordHistory.length === 0 ? (
              <Box textAlign="center" color="inherit" padding="l">
                No history found
              </Box>
            ) : (
              <Steps
                ariaLabel="Record history timeline"
                renderStep={(step) => {
                  const cfg = STEP_ICON[step.status] ?? STEP_ICON.pending
                  return {
                    header: step.header,
                    details: <Box margin={{ bottom: "xl", left: "s" }}>{step.details}</Box>,
                    icon: (
                      <Icon
                        name={cfg.name}
                        variant={cfg.variant}
                        ariaLabel={step.statusIconAriaLabel}
                      />
                    ),
                  }
                }}
                steps={recordHistory.map((entry): StepsProps.Step => {
                  const historyRecordFailed = entry.record.DataQualityEvaluationResult === "Failed"
                  const failureReason = (() => {
                    const rulesFail = entry.record.DataQualityRulesFail
                    if (rulesFail) {
                      const rules = typeof rulesFail === "string" ? [rulesFail] : rulesFail
                      if (Array.isArray(rules) && rules.length > 0) {
                        if (rules.some((r: string) => r.includes("IsPrimaryKey"))) {
                          return "Duplicate primary key"
                        }
                        return `Failed: ${rules[0]}`
                      }
                    }
                    return "Validation failed"
                  })()

                  return {
                    status: runStatusIndicator(entry.run.status),
                    statusIconAriaLabel: entry.run.status,
                    header: "",
                    details: (
                      <Container disableContentPaddings>
                        <Box padding="s">
                          <SpaceBetween size="xs">
                            <SpaceBetween size="xxs">
                              <Flex>
                                <Box fontWeight="bold" display="inline">
                                  {new Date(entry.run.createdAt).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </Box>
                                <Badge
                                  color={
                                    entry.run.status === RunStatus.Completed
                                      ? "green"
                                      : entry.run.status === RunStatus.Failed
                                        ? "red"
                                        : "severity-medium"
                                  }
                                >
                                  {entry.run.status}
                                </Badge>
                              </Flex>
                              <Box color="text-status-inactive" display="inline">
                                {entry.run.fileName || `Run ${entry.run.runId.slice(0, 8)}`}
                              </Box>
                            </SpaceBetween>
                            {entry.run.status === RunStatus.Completed ? (
                              <StatusIndicator type="success">
                                Run completed successfully
                              </StatusIndicator>
                            ) : (
                              <SpaceBetween size="xxs">
                                {entry.recordCount !== undefined && entry.recordCount > 0 && (
                                  <Box
                                    color={
                                      historyRecordFailed
                                        ? "text-status-error"
                                        : "text-status-inactive"
                                    }
                                    fontSize="body-s"
                                  >
                                    {historyRecordFailed
                                      ? `Failed record of ${entry.recordCount} quarantined`
                                      : `Companion record of ${entry.recordCount} quarantined`}
                                  </Box>
                                )}
                                {historyRecordFailed && <Alert type="error">{failureReason}</Alert>}
                              </SpaceBetween>
                            )}
                          </SpaceBetween>
                        </Box>
                      </Container>
                    ),
                  }
                })}
              />
            )}
          </SpaceBetween>
        </Modal>
      )}
    </Modal>
  )
}

export default QuarantineRecordsTable
